/**
 * Admin V3 — Booking incident status-update modal (AV3 A-4)
 *
 * Operator updates an incident's status via
 * PATCH /admin/control-tower/incidents/{id} (RBAC: admin:bookings:incident).
 *
 * Allowed transitions are driven by the canonical enum map
 * (incident_status_transitions from GET /admin/control-tower/enums); the backend
 * re-validates the transition and logs the operator action. resolution_notes is
 * offered for the "resolved" transition. Parity with the legacy V2 incident
 * status flow. No new business logic, no backend change.
 */
import React, { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import { useToast } from '../../shared/hooks/useToast';
import { t, DEFAULT_LOCALE } from '../../i18n';

const inputCls =
  'w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 ' +
  'placeholder:text-slate-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100';

const BookingIncidentUpdateModal = ({ open, incident, statusTransitions = {}, onClose, onDone, locale = DEFAULT_LOCALE }) => {
  const incidentId = incident?.id;
  const m = useAdminMutation(`/admin/control-tower/incidents/${incidentId || '_'}`, 'patch');
  const { showSuccess, showError } = useToast();

  const [status, setStatus] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');

  if (!open || !incident) return null;

  const allowed = statusTransitions[incident.status] || [];
  const valid = Boolean(status);

  const submit = () => {
    m.mutate(
      {
        status,
        resolution_notes: resolutionNotes.trim() || null,
      },
      {
        onSuccess: () => {
          showSuccess(t('bookings.incidentUpdate.ok', locale));
          if (onDone) onDone();
          onClose();
        },
        onError: (e) => showError(e?.response?.data?.detail || t('bookings.incidentUpdate.error', locale)),
      },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="adminv3-booking-incident-update-modal">
      <div className="w-[520px] max-w-[94vw] rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-[17px] font-semibold text-slate-900">{t('bookings.incidentUpdate.title', locale)}</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-testid="adminv3-booking-incident-update-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mb-3 text-[12px] text-slate-500">
          {t('bookings.incidentUpdate.current', locale).replace('{status}', t(`bookings.incidentStatus.${incident.status}`, locale))}
        </p>

        <div className="space-y-3">
          <div>
            <label className="text-[12px] font-semibold text-slate-600">{t('bookings.incidentUpdate.newStatusLabel', locale)}</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className={inputCls}
              data-testid="adminv3-booking-incident-update-status"
            >
              <option value="">{t('bookings.incidentUpdate.newStatusPlaceholder', locale)}</option>
              {allowed.map((s) => (
                <option key={s} value={s}>{t(`bookings.incidentStatus.${s}`, locale)}</option>
              ))}
            </select>
            {allowed.length === 0 && (
              <p className="mt-1 text-[12px] text-slate-400" data-testid="adminv3-booking-incident-update-no-transitions">
                {t('bookings.incidentUpdate.noTransitions', locale)}
              </p>
            )}
          </div>

          {status === 'resolved' && (
            <div>
              <label className="text-[12px] font-semibold text-slate-600">{t('bookings.incidentUpdate.notesLabel', locale)}</label>
              <textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                rows={2}
                className={inputCls}
                data-testid="adminv3-booking-incident-update-notes"
              />
            </div>
          )}
        </div>

        {!valid && (
          <p className="mt-3 text-[12px] text-rose-600" data-testid="adminv3-booking-incident-update-missing">
            {t('bookings.incidentUpdate.required', locale)}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="adminv3-booking-incident-update-dismiss">
            {t('bookings.incidentUpdate.cancel', locale)}
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={submit}
            disabled={!valid || m.loading}
            data-testid="adminv3-booking-incident-update-confirm"
          >
            {m.loading && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            {t('bookings.incidentUpdate.confirm', locale)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BookingIncidentUpdateModal;
